import { useEffect, useMemo, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { useIsPortrait } from '../../../../util/breakpoints'
import { isInScrollRange } from '../../../../util/scrollPercentage'
import { Poster, ScrollVideoProps, VideoContainer } from './ScrollVideo'
import { getFirstFrameUrl, getLastFrameUrl, getMiddleFrameUrl } from '../../../../util/path'

export default function ScrollVideoByPlayback(props: ScrollVideoProps) {
  const videoRef = useRef<ReactPlayer>(null)

  const [videoIsReady, setVideoIsReady] = useState<boolean>(false)
  const [videoIsPlaying, setVideoIsPlaying] = useState<boolean>(false)
  const [videoPlaybackRate, setVideoPlaybackRate] = useState<number>(1)
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null)
  const [renderVideoPlayer, setRenderVideoPlayer] = useState<boolean>(false)
  const isPortrait = useIsPortrait()

  const videoUrl = isPortrait ? props.videoPortrait : props.videoLandscape
  const scrollPercentageRef = useRef(props.scrollPercentage)
  const videoIsReadyRef = useRef(videoIsReady)

  useEffect(() => {
    scrollPercentageRef.current = props.scrollPercentage
    videoIsReadyRef.current = videoIsReady
  }, [props.scrollPercentage, videoIsReady])

  useEffect(() => {
    setRenderVideoPlayer(true)
  }, [])

  useEffect(() => {
    const cleanup = () => {
      if (intervalId) {
        clearInterval(intervalId)
        setIntervalId(null)
      }
    }

    if (props.isActive) {
      setIntervalId(
        setInterval(() => {
          if (
            isInScrollRange(scrollPercentageRef.current) &&
            videoIsReadyRef.current &&
            videoRef.current
          ) {
            const currentProgressVideo =
              videoRef.current.getCurrentTime() / videoRef.current.getDuration()
            const difference = Math.abs(
              currentProgressVideo - scrollPercentageRef.current
            )

            if (currentProgressVideo > scrollPercentageRef.current) {
              setVideoIsPlaying(false)

              if (difference > 0.01) {
                videoRef.current.seekTo(scrollPercentageRef.current, 'fraction')
              }
            } else {
              // if difference less than 0.01, use 2. increase it exponentially to 6 if difference is bigger
              const playbackRate = Math.min(2 + (difference - 0.01) * 10, 6)
              setVideoPlaybackRate(playbackRate)
              setVideoIsPlaying(true)
            }
          }
        }, 50)
      )
    } else {
      cleanup()
    }

    return cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isActive])

  const memoizedPlayer = useMemo(() => {
    return (
      <ReactPlayer
        className="video-player"
        onReady={() => setVideoIsReady(true)}
        ref={videoRef}
        url={videoUrl ?? ''}
        playing={videoIsPlaying}
        playbackRate={videoPlaybackRate}
        width="100%"
        height="100%"
        style={{ opacity: videoIsReady ? 1 : 0 }}
        playsinline
        muted
        aria-label={props.alt}
        config={{
          file: {
            attributes: {
              preload: 'auto',
            },
          },
        }}
      />
    )
  }, [
    videoRef,
    videoUrl,
    videoIsReady,
    videoIsPlaying,
    videoPlaybackRate,
    props.alt,
  ])

  return (
    <VideoContainer>
      {renderVideoPlayer && memoizedPlayer}

      <Poster
        posterIsVisible={props.scrollPercentage <= 1}
        posterUrl={getLastFrameUrl(videoUrl)}
      />
      <Poster
        posterIsVisible={props.scrollPercentage <= 0.66}
        posterUrl={getMiddleFrameUrl(videoUrl)}
      />
      <Poster
        posterIsVisible={props.scrollPercentage <= 0.33}
        posterUrl={getFirstFrameUrl(videoUrl)}
      />
    </VideoContainer>
  )
}
